import React from "react"
import ArticleMeta from "../../components/article-meta"
import CommentsForm from "../../components/comments-form"
import Layout from "../../components/layout/layout"
import SEO from "../../components/seo"
import { Articles } from "../../data/articles"

const article = Articles.getBySlug("deep-knowledge")

export default () => (
  <Layout>
    <SEO
      title={article.title}
      keywords={[article.title, ...article.categories]}
    />
    <div id="section">
      <div className="main-article">
        <ArticleMeta article={article} />

        <div className="article-content">
          <img
            src={article.image}
            className="attachment-wide-thumbnail"
            alt={article.title}
          />
          <p>
            The best leaders understand that the current success of their
            business, and any future innovation, depends upon the “deep smarts”
            of their employees — the business-critical, experience-based
            knowledge that employees carry with them. Leaders with a passion for
            developing employees’ skills, and those who understand the need to
            transfer knowledge among generations of workers, know how important
            it is to link in-house education to strategic planning.
          </p>
          <p>
            Take architectural and engineering firm EYP as an example. Leila
            Kamal, vice president for design and expertise, not only reports to
            the CEO but also is a member of the board. An architect herself, she
            brings great credibility and visibility to programs of learning and
            knowledge exchange. An early in-house program called A16 treated 16
            junior architects to 16 weeks of intensive training, including
            knowledge mentorship from highly experienced architects. The
            educational concepts developed in that program have since evolved
            into a larger learning program called EYP University, which provides
            an average of 20 courses a year for architects, engineers, and a
            combination of the two. Instructors combine traditional lectures
            with hands-on activities, including challenging participants to
            solve a difficult problem before they are presented with the
            solution. This mode of learning embodies what educational researcher
            Robert Bjork call “desirable difficulty,” compelling learners to
            fully engage mentally to discover nonobvious answers to problems.
            Senior thought leaders who are mentors at EYP have to submit a
            learning plan for their mentees, describing not only goals but also
            specific tasks the learner will undertake, such as a client
            presentation. The learners, in turn, provide performance evaluations
            on their mentors’ skills as a mentor or on their ability to teach
            and share knowledge through the EYP U curriculum. Moreover, the
            mentoring flows “up and down” — from juniors to seniors, as well as
            vice versa. For example, new hires tend to come in knowing building
            information modeling from school, and can tutor experienced
            designers and architects in how to incorporate their deep practical
            knowledge into the software.
          </p>

          <p>
            Despite its relatively small size (about 600 employees), EYP also
            supports in-house research projects proposed by employees, which are
            selected through a competitive process. These knowledge-generation
            projects, always aimed at solving a challenge faced by clients, are
            intended to help differentiate EYP from competitors. For example,
            one such project focuses on the interaction between a building’s
            functionality and the well-being and productiveness of its
            occupants. Another aims to create flexible environments that can be
            adjusted in response to information gathered by sensors placed
            around the building that record how people are using a given space.
            For example, temperature and lighting in a room could lower or raise
            depending on how many people are present and whether they are
            viewing a presentation or reading.
          </p>
          <p>
            Contrast the example above with an organization where one manager
            noted: “I think we are very good at managing information, but not
            very good at managing knowledge.” It’s an astute observation.
            Knowledge differs from information in that the former is at least
            partially based on experience. Organizations that are proactive
            about managing the flow of knowledge focus on several potentially
            essential ingredients to future success, such as:
          </p>
          <ul>
            <li>
              <strong>Retaining experience-based know-how</strong>, including
              not only technical knowledge but also so-called “softer” skills,
              such as project management and maintaining critical relationships
              inside and outside the organization that have been built up over
              years. For example, many top sales people know clients personally;
              subject matter experts know others in their field. Such trusted
              relationships facilitate communications and speed decision making.
            </li>
            <li>
              <strong>Helping mentors pass along their expertise</strong> more
              effectively and helping mentees learn more efficiently. Mentors
              can teach through practical problem sets and hands-on diagnoses
              instead of lectures and presentations. Newcomers can learn more
              efficiently by keeping “learning logs” that chronicle their
              experiences and through scheduled feedback sessions with their
              mentors.
            </li>
            <li>
              <strong>Encouraging reverse mentoring</strong> from newcomers to
              elders, such as having newcomers tutor experienced personnel in
              social media.
            </li>
            <li>
              <strong>Generating new knowledge</strong> by conducting research,
              benchmarking, or bringing in “resident” artists or scientists
              whose interactions with employees can spark creativity.
            </li>
          </ul>

          <CommentsForm title={article.title} />
        </div>
      </div>
    </div>
  </Layout>
)
